/**
 * Blocks the app on phone-sized screens.
 *
 * ATS India is a desktop tool — dense tables, multi-column forms, an admin
 * that assumes a mouse — so rather than ship a broken small-screen layout it
 * shows a plain "use a computer" message below a phone-width viewport.
 *
 * Width-based, not user-agent: a real viewport measurement is reliable, while
 * UA strings are spoofable and constantly out of date. It reacts to resize and
 * rotation, so rotating a phone to landscape or narrowing a desktop window is
 * re-evaluated live. The threshold is one number, below, if it needs tuning.
 *
 * Wraps everything including the login screen — the point is that the app does
 * not open on a phone at all, not that a signed-in view is hidden.
 */
import { useEffect, useState } from 'react';
import { Monitor } from 'lucide-react';

/** Phones are below this; tablets and laptops (>=768px) are allowed through. */
const MOBILE_MAX_WIDTH = 767;
const QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;

export default function MobileGate({ children }: { children: React.ReactNode }) {
  const [blocked, setBlocked] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(QUERY).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(QUERY);
    const onChange = () => setBlocked(mq.matches);
    mq.addEventListener('change', onChange);
    // Sync once in case the width changed between render and effect.
    onChange();
    return () => mq.removeEventListener('change', onChange);
  }, []);

  if (!blocked) return <>{children}</>;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-6 text-center">
      <div className="max-w-sm">
        <Monitor className="mx-auto h-12 w-12 text-indigo-600" />
        <h1 className="mt-4 text-lg font-semibold text-gray-900">
          Please use a desktop or laptop
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          ATS India isn’t available on mobile devices. Open it on a computer
          with a larger screen to sign in.
        </p>
      </div>
    </div>
  );
}
